import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Grid } from '@mui/material';
import { Close as CloseIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';

// Import styled components
import { StyledModal, ModalContent } from './StyledComponents.js';

// Import child components
import ToggleButtons from './ToggleButtons.jsx';
import OCRSection from './OCRSection.jsx';
import PDFViewer from './PDFViewer.jsx';
import AccountsSection from './AccountsSection.jsx';
import CustomsAgentInvoiceSection from './CustomsAgentInvoiceSection.jsx';
import SortingCodesSection from './SortingCodesSection.jsx';
import ImportManifestDetailsSection from './ImportManifestDetailsSection.jsx';
import TotalsAndActionsSection from './TotalsAndActionsSection.jsx';

// Import services and utilities
import DocumentService from '../../../../Services/documents.service.js';
import EnumsService from '../../../../Services/enums.service.js';
import { toast } from 'react-toastify';
import { calculateVatAmountV2 } from '../../../../Utils/GetVatValue.js';
import Swal from 'sweetalert2';
import AddAccountCardModal from '../../../../Components/Widgets/AddAccountCardModal/AddAccountCardModal';
import CustomMonthDatePicker from '../../../../Components/Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import dayjs from 'dayjs';

const ImportManifestEntryModal = ({
    open,
    onClose,
    direction,
    t,
    client,
    token,
    accountCardOptions = [],
    sortCodeIdOptions = [],
    year,
    month,
    onDataRefresh,
    fetchEnums,
    setLoading,
    palCodes = [],
    onAccountCardAdded
}) => {
    // helper to generate initial form state
    const getInitialFormData = () => {
        // Get useRegistrationMonth toggle from localStorage (same as JournalEntry.jsx)
        const useRegistrationMonth = localStorage.getItem('UseRegistrationMonth') === "true";
        // Calculate initial dates based on year/month props (day 10, matching backend pattern)
        const initialReportingMonthDate = new Date(Date.UTC(year, month - 1, 10));
        const initialRegistrationMonthDate = new Date(Date.UTC(year, month - 1, 10));

        return {
            // Accounts
            expense: '',
            inputVat: '3',
            customsAgent: '',
            customsAgentId: '',

            // Customs Agent Invoice
            invoiceNumber: '',
            invoiceDate: new Date(), // Set default to current date
            commission: '0.00',
            storageAtCustoms: '0.00',
            seaAirFreight: '0.00',
            variousVatLiable: '0.00',
            variousVatExempt: '0.00',
            roundingDifference: '0.00',

            // Sorting Codes
            manifest: '350',
            liableExpenses: '200',
            exemptExpenses: '211',

            // Import Manifest Details
            manifestNumber: '',
            manifestDate: new Date(),
            customsPurchaseTax: '0.00',
            importManifestVat: '0.00',
            computerFee: '0.00',
            securityCheckFee: '0.00',
            variousFees: '0.00',
            totalManifest: '0.00',
            totalIncludingVat: '0.00',
            totalVatLiable: '0.00',
            totalVat: '0.00',
            totalToPay: '0.00',

            // Month Dates
            reportingMonthDate: initialReportingMonthDate,
            registrationMonthDate: initialRegistrationMonthDate
        };
    };

    // Form state
    const [formData, setFormData] = useState(getInitialFormData());

    // OCR state - separate documents for each type
    const [importManifestDocuments, setImportManifestDocuments] = useState([]);
    const [customsAgentInvoiceDocuments, setCustomsAgentInvoiceDocuments] = useState([]);
    const [isProcessingImportManifestOCR, setIsProcessingImportManifestOCR] = useState(false);
    const [isProcessingCustomsAgentInvoiceOCR, setIsProcessingCustomsAgentInvoiceOCR] = useState(false);

    // Add automatic/manual filling toggle state
    const [autoFilling, setAutoFilling] = useState(false);

    // Add iframe visibility state
    const [showPdfIframe, setShowPdfIframe] = useState(true);
    const [uploadedDocumentUrl, setUploadedDocumentUrl] = useState(null);

    // Add OCR type selection state
    const [selectedOcrType, setSelectedOcrType] = useState('importManifest'); // 'importManifest' or 'customsAgentInvoice'

    // Add document navigation state for iframe
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    const [documentUrls, setDocumentUrls] = useState([]);
    const [importManifestDocumentUrls, setImportManifestDocumentUrls] = useState([]);
    const [customsAgentInvoiceDocumentUrls, setCustomsAgentInvoiceDocumentUrls] = useState([]);
    const [showDocumentNavigation, setShowDocumentNavigation] = useState(false);
    
    // Ref to track currently active URL to prevent premature revocation
    const activeUrlRef = useRef(null);
    const allActiveUrlsRef = useRef(new Set());

    // Validation errors state
    const [validationErrors, setValidationErrors] = useState({});

    // Reporting month date picker key for forcing re-render
    const [reportingMonthDatePickerKey, setReportingMonthDatePickerKey] = useState(0);

    // AddAccountCardModal state
    const [editedAddAccountCard, setEditedAddAccountCard] = useState(false);
    const [isEditAccountCard, setIsEditAccountCard] = useState(false);
    const [addAccountCardType, setAddAccountCardType] = useState('expense'); // Track which field triggered the modal
    const [cardIdOption, setCardIdOption] = useState(1);
    const [newAccountCard, setNewAccountCard] = useState({
        accountCodeName: '',
        accountCodeId: '',
        accountCaseId: '',
    });

    // Fetch next card ID from API when client and token are available
    useEffect(() => {

        if (!open || !client?.caseId || !token) {
            setCardIdOption(1);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const nextId = await EnumsService.GetNextCardId(client.caseId, null, token);
                if (!cancelled) setCardIdOption(nextId);
            } catch {
                if (!cancelled) setCardIdOption(1);
            }
        })();
        return () => { cancelled = true; };
    }, [open, client?.caseId, token, accountCardOptions.length]);

    // Handle when a new account card is added
    const handleChangeAfterAdd = async (newlyAddedCardId, fieldName, addedAccountCaseId = null) => {
        if (typeof onAccountCardAdded === 'function') {
            try {
                await Promise.resolve(onAccountCardAdded(newlyAddedCardId, addedAccountCaseId ?? '', undefined));
            } catch (e) {
                console.error('Error refetching account card options:', e);
            }
        } else if (fetchEnums && typeof fetchEnums === 'function') {
            try {
                await fetchEnums();
            } catch (e) {
                console.error('Error refetching account card options:', e);
            }
        }
        handleInputChange(fieldName, newlyAddedCardId);
        if (fieldName === 'customsAgent' && addedAccountCaseId != null && addedAccountCaseId !== '') {
            handleInputChange('customsAgentId', addedAccountCaseId);
        }
        setEditedAddAccountCard(false);
    };

    // Handle AddItem selection
    const handleAddItemSelection = (fieldName, searchTerm = '', initialAccountCaseId = '') => {
        setEditedAddAccountCard(true);
        setIsEditAccountCard(false);
        setAddAccountCardType(fieldName);
        setNewAccountCard({
            accountCodeName: searchTerm || '',
            accountCodeId: cardIdOption.toString(),
            accountCaseId: initialAccountCaseId || '',
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };

            // Clear validation error when field is filled
            if (validationErrors[field]) {
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[field];
                    return newErrors;
                });
            }

            // Auto-calculate totalManifest when any of the component fields change
            if (['customsPurchaseTax', 'importManifestVat', 'computerFee', 'securityCheckFee', 'variousFees'].includes(field)) {
                const customsPurchaseTax = field === 'customsPurchaseTax' ? (parseFloat(value) || 0) : (parseFloat(prev.customsPurchaseTax) || 0);
                const importManifestVat = field === 'importManifestVat' ? (parseFloat(value) || 0) : (parseFloat(prev.importManifestVat) || 0);
                const computerFee = field === 'computerFee' ? (parseFloat(value) || 0) : (parseFloat(prev.computerFee) || 0);
                const securityCheckFee = field === 'securityCheckFee' ? (parseFloat(value) || 0) : (parseFloat(prev.securityCheckFee) || 0);
                const variousFees = field === 'variousFees' ? (parseFloat(value) || 0) : (parseFloat(prev.variousFees) || 0);

                const totalManifest = customsPurchaseTax + importManifestVat + computerFee + securityCheckFee + variousFees;
                newData.totalManifest = totalManifest.toFixed(2);
            }

            // Auto-calculate TotalVatLiable when any of the component fields change
            if (['variousVatLiable', 'storageAtCustoms', 'commission'].includes(field)) {
                const variousVatLiable = field === 'variousVatLiable' ? (parseFloat(value) || 0) : (parseFloat(prev.variousVatLiable) || 0);
                const storageAtCustoms = field === 'storageAtCustoms' ? (parseFloat(value) || 0) : (parseFloat(prev.storageAtCustoms) || 0);
                const commission = field === 'commission' ? (parseFloat(value) || 0) : (parseFloat(prev.commission) || 0);

                const totalVatLiable = variousVatLiable + storageAtCustoms + commission;
                newData.totalVatLiable = totalVatLiable.toFixed(2);
            }

            // Calculate TotalIncludingVAT = SeaAirFreight + VariousVatExempt + TotalManifest + RoundingDifference
            // This should be recalculated when any of these fields change
            if (['seaAirFreight', 'variousVatExempt', 'totalManifest', 'roundingDifference'].includes(field) ||
                ['customsPurchaseTax', 'importManifestVat', 'computerFee', 'securityCheckFee', 'variousFees'].includes(field)) {

                const seaAirFreight = field === 'seaAirFreight' ? (parseFloat(value) || 0) : (parseFloat(newData.seaAirFreight) || 0);
                const variousVatExempt = field === 'variousVatExempt' ? (parseFloat(value) || 0) : (parseFloat(newData.variousVatExempt) || 0);
                const totalManifest = field === 'totalManifest' ? (parseFloat(value) || 0) : (parseFloat(newData.totalManifest) || 0);
                const roundingDifference = field === 'roundingDifference' ? (parseFloat(value) || 0) : (parseFloat(newData.roundingDifference) || 0);

                const totalIncludingVat = seaAirFreight + variousVatExempt + totalManifest + roundingDifference;
                newData.totalIncludingVat = totalIncludingVat.toFixed(2);
            }

            // Calculate TotalVAT using calculateVatAmount on TotalVatLiable
            const totalVatLiable = parseFloat(newData.totalVatLiable || 0);
            const totalVat = calculateVatAmountV2(totalVatLiable, 100, newData.invoiceDate);
            newData.totalVat = totalVat.toFixed(2);

            // Calculate TotalToPay = TotalIncludingVAT + TotalVatLiable + TotalVAT
            const totalIncludingVat = parseFloat(newData.totalIncludingVat || 0);
            const totalToPay = totalIncludingVat + totalVatLiable + totalVat;
            newData.totalToPay = totalToPay.toFixed(2);

            return newData;
        });
    };

    const handleYearMonthChange = (field, value) => {
        // For year and month, we can store them in formData for form submission
        // but they will be controlled by props for display
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle reporting month date change
    const handleReportingMonthDateChange = (date) => {
        if (date) {
            // Set to day 10 of the month (matching backend pattern)
            const newDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 10));
            handleInputChange('reportingMonthDate', newDate);
        }
    };

    // Handle registration month date change
    const handleRegistrationMonthDateChange = (date) => {
        if (date) {
            // Set to day 10 of the month (matching backend pattern)
            const newDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 10));
            handleInputChange('registrationMonthDate', newDate);
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.expense) {
            errors.expense = t('ExpenseRequired');
        }
        if (!formData.inputVat) {
            errors.inputVat = t('InputVATRequired');
        }
        if (!formData.customsAgent) {
            errors.customsAgent = t('CustomsAgentRequired');
        }
        if (!formData.manifest) {
            errors.manifest = t('ManifestRequired');
        }
        if (!formData.liableExpenses) {
            errors.liableExpenses = t('LiableExpensesRequired');
        }
        if (!formData.exemptExpenses) {
            errors.exemptExpenses = t('ExemptExpensesRequired');
        }

        if (!formData.customsAgentId) {
            errors.customsAgentId = t('CustomsAgentIdRequired');
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const clearAllFields = () => {
        setFormData(getInitialFormData());
        setValidationErrors({});
        // Clean up document URLs and clear documents
        // Revoke all URLs that are tracked
        allActiveUrlsRef.current.forEach(url => {
            if (url && typeof url === 'string' && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        });
        allActiveUrlsRef.current.clear();
        activeUrlRef.current = null;
        
        setImportManifestDocuments([]);
        setCustomsAgentInvoiceDocuments([]);
        setDocumentUrls([]);
        setImportManifestDocumentUrls([]);
        setCustomsAgentInvoiceDocumentUrls([]);
        setUploadedDocumentUrl(null);
        setCurrentDocumentIndex(0);
        setShowDocumentNavigation(false);
        // Reset reporting month date picker key
        setReportingMonthDatePickerKey(prev => prev + 1);
    };

    const handleAttemptClose = async () => {
        const hasMeaningfulData = () => {
            const nonEmptyStrings = [
                formData.expense,
                formData.customsAgent,
                formData.customsAgentId,
                formData.invoiceNumber,
                formData.manifestNumber
            ].some(v => (v || '').toString().trim() !== '');

            const numericFields = [
                formData.commission,
                formData.storageAtCustoms,
                formData.seaAirFreight,
                formData.variousVatLiable,
                formData.variousVatExempt,
                formData.roundingDifference,
                formData.customsPurchaseTax,
                formData.importManifestVat,
                formData.computerFee,
                formData.securityCheckFee,
                formData.variousFees
            ].some(v => parseFloat(v || '0') !== 0);

            const hasDocs = (importManifestDocuments && importManifestDocuments.length > 0) || 
                           (customsAgentInvoiceDocuments && customsAgentInvoiceDocuments.length > 0);

            return nonEmptyStrings || numericFields || hasDocs;
        };

        if (!hasMeaningfulData()) {
            onClose();
            return;
        }

        const result = await Swal.fire({
            title: t('ResetFieldsQuestion') || 'Reset fields before closing?',
            text: t('ResetFieldsQuestionText') || 'Would you like to reset all fields before closing the modal?',
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: t('ResetAndClose') || 'Reset and Close',
            denyButtonText: t('CloseWithoutReset') || 'Close without Reset',
            cancelButtonText: t('Cancel') || 'Cancel'
        });

        if (result.isConfirmed) {
            clearAllFields();
            onClose();
        } else if (result.isDenied) {
            onClose();
        } else {
            // cancelled: do nothing
        }
    };

    const handleUpdate = async () => {
        // Validate form before proceeding
        if (!validateForm()) {
            return;
        }

        try {
            // Get useRegistrationMonth toggle from localStorage (same as JournalEntry.jsx)
            const useRegistrationMonth = localStorage.getItem('UseRegistrationMonth') === "true";
            
            // Use dates from formData if available, otherwise calculate from year/month props
            // Use day 10 of the month (matching backend pattern)
            const reportingMonthDate = formData.reportingMonthDate 
                ? new Date(Date.UTC(formData.reportingMonthDate.getFullYear(), formData.reportingMonthDate.getMonth(), 10))
                : new Date(Date.UTC(year, month - 1, 10));
            
            const registrationMonthDate = formData.registrationMonthDate 
                ? new Date(Date.UTC(formData.registrationMonthDate.getFullYear(), formData.registrationMonthDate.getMonth(), 10))
                : new Date(Date.UTC(year, month - 1, 10));

            // Calculate amounts based on the logic you specified
            const entry1Amount = (parseFloat(formData.seaAirFreight) || 0) + (parseFloat(formData.variousVatExempt) || 0);
            const totalVatLiable = parseFloat(formData.totalVatLiable || 0);
            const totalVat = calculateVatAmountV2(totalVatLiable, 100, formData.invoiceDate);
            const entry2Amount = totalVat + parseFloat(formData.totalVatLiable) || 0;
            const entry3Amount = (parseFloat(formData.customsPurchaseTax) || 0) + (parseFloat(formData.computerFee) || 0) + (parseFloat(formData.securityCheckFee) || 0) + (parseFloat(formData.variousFees) || 0);
            const entry4Amount = parseFloat(formData.importManifestVat) || 0;

            // Prepare the ImportManifestEntryDTO structure
            const importManifestEntryDTO = {
                CaseId: client.caseId,
                AccountantId: client.accountantId,
                DocumentDate: formData.invoiceDate || new Date(),
                UploadDate: new Date(),
                ReportingMonthDate: reportingMonthDate, // Day 10 of the selected month/year, respecting the toggle
                AddAsProcessed: false,
                Documents: null, // Can be added later if needed
                OurDocument: false,
                ForeignCurrencyAmount: null,
                ForeignCurrencyCode: null,
                IsLocked: false,

                // Only include sections that have data
                AccountsSection: (formData.expense || formData.inputVat || formData.customsAgent || formData.customsAgentId) ? {
                    Expense: formData.expense || null,
                    ExpenseName: formData.expenseName || null,
                    InputVat: formData.inputVat || null,
                    CustomsAgent: formData.customsAgent || null,
                    CustomsAgentId: formData.customsAgentId || null
                } : null,

                CustomsAgentInvoiceSection: (formData.invoiceNumber || formData.commission || formData.storageAtCustoms ||
                    formData.seaAirFreight || formData.variousVatLiable || formData.variousVatExempt || formData.roundingDifference) ? {
                    InvoiceNumber: formData.invoiceNumber || null,
                    InvoiceDate: formData.invoiceDate || null,
                    Commission: formData.commission ? parseFloat(formData.commission) : null,
                    StorageAtCustoms: formData.storageAtCustoms ? parseFloat(formData.storageAtCustoms) : null,
                    SeaAirFreight: formData.seaAirFreight ? parseFloat(formData.seaAirFreight) : null,
                    VariousVatLiable: formData.variousVatLiable ? parseFloat(formData.variousVatLiable) : null,
                    VariousVatExempt: formData.variousVatExempt ? parseFloat(formData.variousVatExempt) : null,
                    RoundingDifference: formData.roundingDifference ? parseFloat(formData.roundingDifference) : null
                } : null,

                SortingCodesSection: (formData.manifest || formData.liableExpenses || formData.exemptExpenses) ? {
                    Manifest: formData.manifest || null,
                    LiableExpenses: formData.liableExpenses || null,
                    ExemptExpenses: formData.exemptExpenses || null
                } : null,

                ImportManifestDetailsSection: (formData.manifestNumber || formData.customsPurchaseTax ||
                    formData.importManifestVat || formData.computerFee || formData.securityCheckFee ||
                    formData.variousFees || formData.totalManifest) ? {
                    ManifestNumber: formData.manifestNumber || null,
                    ManifestDate: formData.manifestDate || null,
                    CustomsPurchaseTax: formData.customsPurchaseTax ? parseFloat(formData.customsPurchaseTax) : null,
                    ImportManifestVat: formData.importManifestVat ? parseFloat(formData.importManifestVat) : null,
                    ComputerFee: formData.computerFee ? parseFloat(formData.computerFee) : null,
                    SecurityCheckFee: formData.securityCheckFee ? parseFloat(formData.securityCheckFee) : null,
                    VariousFees: formData.variousFees ? parseFloat(formData.variousFees) : null,
                    TotalManifest: formData.totalManifest ? parseFloat(formData.totalManifest) : null
                } : null,

                // Add calculated amounts for each entry
                Entry1Amount: entry1Amount,
                Entry2Amount: entry2Amount,
                Entry3Amount: entry3Amount,
                Entry4Amount: entry4Amount
            };

            // Remove null sections to only send sections with data
            Object.keys(importManifestEntryDTO).forEach(key => {
                if (importManifestEntryDTO[key] === null && key.endsWith('Section')) {
                    delete importManifestEntryDTO[key];
                }
            });


            // Create FormData to send to the API
            const formDataToSend = new FormData();

            // Add basic fields
            formDataToSend.append('CaseId', importManifestEntryDTO.CaseId);
            formDataToSend.append('AccountantId', importManifestEntryDTO.AccountantId);
            formDataToSend.append('DocumentDate', importManifestEntryDTO.DocumentDate.toISOString());
            formDataToSend.append('UploadDate', importManifestEntryDTO.UploadDate.toISOString());
            formDataToSend.append('ReportingMonthDate', importManifestEntryDTO.ReportingMonthDate.toISOString());
            // Add RegistrationMonthDate if available
            if (registrationMonthDate) {
                formDataToSend.append('RegistrationMonthDate', registrationMonthDate.toISOString());
            }
            formDataToSend.append('AddAsProcessed', importManifestEntryDTO.AddAsProcessed.toString());
            formDataToSend.append('OurDocument', importManifestEntryDTO.OurDocument.toString());
            formDataToSend.append('IsLocked', importManifestEntryDTO.IsLocked.toString());

            // Add sections as JSON strings
            if (importManifestEntryDTO.AccountsSection) {
                formDataToSend.append('AccountsSection', JSON.stringify(importManifestEntryDTO.AccountsSection));
            }
            if (importManifestEntryDTO.CustomsAgentInvoiceSection) {
                formDataToSend.append('CustomsAgentInvoiceSection', JSON.stringify(importManifestEntryDTO.CustomsAgentInvoiceSection));
            }
            if (importManifestEntryDTO.SortingCodesSection) {
                formDataToSend.append('SortingCodesSection', JSON.stringify(importManifestEntryDTO.SortingCodesSection));
            }
            if (importManifestEntryDTO.ImportManifestDetailsSection) {
                formDataToSend.append('ImportManifestDetailsSection', JSON.stringify(importManifestEntryDTO.ImportManifestDetailsSection));
            }

            // Add calculated amounts
            formDataToSend.append('Entry1Amount', importManifestEntryDTO.Entry1Amount.toString());
            formDataToSend.append('Entry2Amount', importManifestEntryDTO.Entry2Amount.toString());
            formDataToSend.append('Entry3Amount', importManifestEntryDTO.Entry3Amount.toString());
            formDataToSend.append('Entry4Amount', importManifestEntryDTO.Entry4Amount.toString());

            // Add documents to FormData (combine both types)
            const allDocuments = [...importManifestDocuments, ...customsAgentInvoiceDocuments];
            if (allDocuments && allDocuments.length > 0) {
                allDocuments.forEach((document, index) => {
                    formDataToSend.append('Documents', document);
                });
            }

            // Use DocumentService to make the API call with FormData
            const response = await DocumentService.CreateImportManifestEntry(formDataToSend, token);

            if (response.ok) {
                const result = await response.json();

                // Check if no entries were created
                if (result.createdEntries === 0) {
                    toast.error(t('ErrorCreatingImportManifestEntry'));
                    return;
                }

                toast.success(t('ImportManifestEntryCreatedSuccessfully'));

                // Refresh data if refresh function is provided
                if (onDataRefresh && typeof onDataRefresh === 'function') {
                    await onDataRefresh();
                }

                // Reset form fields but keep Sorting Codes and InputVAT
                setFormData(prev => ({
                    ...prev,
                    // Keep these fields unchanged
                    manifest: prev.manifest,
                    liableExpenses: prev.liableExpenses,
                    exemptExpenses: prev.exemptExpenses,
                    inputVat: prev.inputVat,

                    // Reset other fields
                    expense: t('ImportExpenses105'),
                    customsAgent: t('CustomsAgent106'),
                    customsAgentId: '',
                    invoiceNumber: '',
                    invoiceDate: new Date(),
                    commission: '0.00',
                    storageAtCustoms: '0.00',
                    seaAirFreight: '0.00',
                    variousVatLiable: '0.00',
                    variousVatExempt: '0.00',
                    roundingDifference: '0.00',
                    manifestNumber: '',
                    manifestDate: new Date(),
                    customsPurchaseTax: '0.00',
                    importManifestVat: '0.00',
                    computerFee: '0.00',
                    securityCheckFee: '0.00',
                    variousFees: '0.00',
                    totalManifest: '0.00',
                    totalIncludingVat: '0.00',
                    totalVatLiable: '0.00',
                    totalVat: '0.00',
                    totalToPay: '0.00',
                    // Reset month dates to current year/month
                    reportingMonthDate: new Date(Date.UTC(year, month - 1, 10)),
                    registrationMonthDate: new Date(Date.UTC(year, month - 1, 10))
                }));

                // Clear documents
                // Revoke all URLs that are tracked
                allActiveUrlsRef.current.forEach(url => {
                    if (url && typeof url === 'string' && url.startsWith('blob:')) {
                        URL.revokeObjectURL(url);
                    }
                });
                allActiveUrlsRef.current.clear();
                activeUrlRef.current = null;
                
                setImportManifestDocuments([]);
                setCustomsAgentInvoiceDocuments([]);
                setDocumentUrls([]);
                setImportManifestDocumentUrls([]);
                setCustomsAgentInvoiceDocumentUrls([]);
                setUploadedDocumentUrl(null);
                setCurrentDocumentIndex(0);
                setShowDocumentNavigation(false);

                // Switch back to manual mode
                setAutoFilling(false);

                // Clear validation errors
                setValidationErrors({});

            } else {
                const errorText = await response.text();
                console.error('Error creating import manifest entry:', errorText);
                toast.error(t('ErrorCreatingImportManifestEntry'));
            }
        } catch (error) {
            console.error('Error updating manifest entry:', error);
            toast.error(t('ErrorUpdatingManifestEntry'));
        }
    };

    // Handle document upload for Import Manifest DocumentDrop
    const handleImportManifestDocumentUpload = (event) => {
        const files = Array.from(event.target.files);
        setImportManifestDocuments(files);

        // Clean up previous URLs that are not currently active
        importManifestDocumentUrls.forEach(url => {
            if (url && url !== activeUrlRef.current && !allActiveUrlsRef.current.has(url)) {
                URL.revokeObjectURL(url);
            }
        });

        // Create URLs for the uploaded documents
        const urls = files.map(doc => URL.createObjectURL(doc));
        urls.forEach(url => allActiveUrlsRef.current.add(url));
        setImportManifestDocumentUrls(urls);

        // Combine with customs agent invoice documents for navigation
        const allDocumentUrls = [...urls, ...customsAgentInvoiceDocumentUrls];
        const allDocuments = [...files, ...customsAgentInvoiceDocuments];

        // Show navigation if we have multiple documents
        if (allDocuments.length > 0) {
            setShowDocumentNavigation(allDocuments.length > 1);
            setCurrentDocumentIndex(0);
            const firstUrl = allDocumentUrls[0];
            activeUrlRef.current = firstUrl;
            setUploadedDocumentUrl(firstUrl);
            // Update documentUrls for PDF viewer with combined array
            setDocumentUrls(allDocumentUrls);
        }
    };

    // Handle document upload for Customs Agent Invoice DocumentDrop
    const handleCustomsAgentInvoiceDocumentUpload = (event) => {
        const files = Array.from(event.target.files);
        setCustomsAgentInvoiceDocuments(files);

        // Clean up previous URLs that are not currently active
        customsAgentInvoiceDocumentUrls.forEach(url => {
            if (url && url !== activeUrlRef.current && !allActiveUrlsRef.current.has(url)) {
                URL.revokeObjectURL(url);
            }
        });

        // Create URLs for the uploaded documents
        const urls = files.map(doc => URL.createObjectURL(doc));
        urls.forEach(url => allActiveUrlsRef.current.add(url));
        setCustomsAgentInvoiceDocumentUrls(urls);

        // Combine with import manifest documents for navigation
        const allDocumentUrls = [...importManifestDocumentUrls, ...urls];
        const allDocuments = [...importManifestDocuments, ...files];

        // Show navigation if we have multiple documents
        if (allDocuments.length > 0) {
            setShowDocumentNavigation(allDocuments.length > 1);
            // If import manifest documents exist, start after them, otherwise start at 0
            const startIndex = importManifestDocuments.length > 0 ? importManifestDocuments.length : 0;
            setCurrentDocumentIndex(startIndex);
            const targetUrl = allDocumentUrls[startIndex];
            activeUrlRef.current = targetUrl;
            setUploadedDocumentUrl(targetUrl);
            // Update documentUrls for PDF viewer with combined array
            setDocumentUrls(allDocumentUrls);
        }
    };

    // Handle iframe visibility toggle
    const handleToggleIframe = () => {
        setShowPdfIframe((prev) => !prev);
    };

    // Handle document navigation - use combined document URLs
    const handlePreviousDocument = () => {
        // Calculate combined URLs on each call to ensure we have the latest
        const allDocumentUrls = [...importManifestDocumentUrls, ...customsAgentInvoiceDocumentUrls];
        
        if (allDocumentUrls.length === 0) return;
        
        // Use functional update to get the latest currentDocumentIndex
        setCurrentDocumentIndex(prevIndex => {
            if (prevIndex > 0) {
                const newIndex = prevIndex - 1;
                const targetUrl = allDocumentUrls[newIndex];
                
                // Validate URL exists and is valid
                if (targetUrl && typeof targetUrl === 'string') {
                    // Mark as active before setting
                    activeUrlRef.current = targetUrl;
                    allActiveUrlsRef.current.add(targetUrl);
                    // Update URL immediately
                    setUploadedDocumentUrl(targetUrl);
                    // Also update documentUrls to keep it in sync
                    setDocumentUrls(allDocumentUrls);
                    return newIndex;
                }
            }
            return prevIndex;
        });
    };

    const handleNextDocument = () => {
        // Calculate combined URLs on each call to ensure we have the latest
        const allDocumentUrls = [...importManifestDocumentUrls, ...customsAgentInvoiceDocumentUrls];
        
        if (allDocumentUrls.length === 0) return;
        
        // Use functional update to get the latest currentDocumentIndex
        setCurrentDocumentIndex(prevIndex => {
            if (prevIndex < allDocumentUrls.length - 1) {
                const newIndex = prevIndex + 1;
                const targetUrl = allDocumentUrls[newIndex];
                
                // Validate URL exists and is valid
                if (targetUrl && typeof targetUrl === 'string') {
                    // Mark as active before setting
                    activeUrlRef.current = targetUrl;
                    allActiveUrlsRef.current.add(targetUrl);
                    // Update URL immediately
                    setUploadedDocumentUrl(targetUrl);
                    // Also update documentUrls to keep it in sync
                    setDocumentUrls(allDocumentUrls);
                    return newIndex;
                }
            }
            return prevIndex;
        });
    };

    // Handle OCR processing with DocumentDrop for Import Manifest
    const handleProcessOCR = async () => {
        if (importManifestDocuments.length === 0) {
            toast.error(t('PleaseUploadAtLeastOneDocument'));
            return;
        }

        setIsProcessingImportManifestOCR(true);
        try {
            const formData = new FormData();
            formData.append('CaseId', client.caseId);
            formData.append('AccountantId', client.accountantId);

            importManifestDocuments.forEach((doc) => {
                formData.append('Documents', doc);
            });

            // Use DocumentService.CreateImportManifestByOCR instead of direct fetch
            const response = await DocumentService.CreateImportManifestByOCR(formData, token);

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.parsed) {
                    // Use the existing document URL from importManifestDocumentUrls array
                    if (importManifestDocumentUrls.length > 0 && currentDocumentIndex < importManifestDocumentUrls.length) {
                        setUploadedDocumentUrl(importManifestDocumentUrls[currentDocumentIndex]);
                        setDocumentUrls(importManifestDocumentUrls);
                    }

                    // Update form with OCR results
                    setFormData(prev => {
                        const newData = {
                            ...prev,
                            manifestNumber: result.parsed.manifest_number || prev.manifestNumber,
                            manifestDate: result.parsed.manifest_date ? new Date(result.parsed.manifest_date.split('/').reverse().join('-')) : prev.manifestDate,
                            customsPurchaseTax: result.parsed.customs_purchase_tax?.toString() || prev.customsPurchaseTax,
                            importManifestVat: result.parsed.import_manifest_vat?.toString() || prev.importManifestVat,
                            computerFee: result.parsed.computer_fee?.toString() || prev.computerFee,
                            securityCheckFee: result.parsed.security_check_fee?.toString() || prev.securityCheckFee,
                            variousFees: result.parsed.various_fees?.toString() || prev.variousFees,
                            totalManifest: result.parsed.total_manifest?.toString() || prev.totalManifest
                        };

                        // Recalculate totals after OCR data update
                        // Calculate totalManifest from component fields
                        const customsPurchaseTax = parseFloat(newData.customsPurchaseTax) || 0;
                        const importManifestVat = parseFloat(newData.importManifestVat) || 0;
                        const computerFee = parseFloat(newData.computerFee) || 0;
                        const securityCheckFee = parseFloat(newData.securityCheckFee) || 0;
                        const variousFees = parseFloat(newData.variousFees) || 0;

                        newData.totalManifest = (customsPurchaseTax + importManifestVat + computerFee + securityCheckFee + variousFees).toFixed(2);

                        // Calculate TotalVatLiable (variousVatLiable + storageAtCustoms + commission)
                        const variousVatLiable = parseFloat(newData.variousVatLiable) || 0;
                        const storageAtCustoms = parseFloat(newData.storageAtCustoms) || 0;
                        const commission = parseFloat(newData.commission) || 0;
                        newData.totalVatLiable = (variousVatLiable + storageAtCustoms + commission).toFixed(2);

                        // Calculate TotalIncludingVAT = SeaAirFreight + VariousVatExempt + TotalManifest + RoundingDifference
                        const seaAirFreight = parseFloat(newData.seaAirFreight) || 0;
                        const variousVatExempt = parseFloat(newData.variousVatExempt) || 0;
                        const totalManifest = parseFloat(newData.totalManifest) || 0;
                        const roundingDifference = parseFloat(newData.roundingDifference) || 0;
                        newData.totalIncludingVat = (seaAirFreight + variousVatExempt + totalManifest + roundingDifference).toFixed(2);

                        // Calculate TotalVAT using calculateVatAmount on TotalVatLiable
                        const totalVatLiable = parseFloat(newData.totalVatLiable) || 0;
                        const totalVat = calculateVatAmountV2(totalVatLiable, 100, newData.invoiceDate);
                        newData.totalVat = totalVat.toFixed(2);

                        // Calculate TotalToPay = TotalIncludingVAT + TotalVatLiable + TotalVAT
                        const totalIncludingVat = parseFloat(newData.totalIncludingVat) || 0;
                        newData.totalToPay = (totalIncludingVat + totalVatLiable + totalVat).toFixed(2);

                        return newData;
                    });

                    // After successful OCR processing, switch back to manual mode to show the filled fields
                    setAutoFilling(false);
                    toast.success(t('CustomsAgentInvoiceProcessedSuccessfully'));
                }
            } else {
                const errorText = await response.text();
                console.error('Import Manifest OCR processing error:', errorText);
                toast.error(t('ErrorProcessingImportManifestOCR') || t('ErrorProcessingDocument'));
            }
        } catch (error) {
            console.error('Error processing Import Manifest OCR:', error);
            toast.error(t('ErrorProcessingImportManifestOCR') || t('ErrorProcessingDocument'));
        } finally {
            setIsProcessingImportManifestOCR(false);
        }
    };

    const handleProcessCustomsAgentInvoiceOCR = async () => {
        if (customsAgentInvoiceDocuments.length === 0) {
            toast.error(t('PleaseUploadAtLeastOneDocument'));
            return;
        }

        setIsProcessingCustomsAgentInvoiceOCR(true);
        try {
            const formData = new FormData();
            formData.append('CaseId', client.caseId);
            formData.append('AccountantId', client.accountantId);

            customsAgentInvoiceDocuments.forEach((doc) => {
                formData.append('Documents', doc);
            });

            // Use DocumentService.CreateCustomsAgentInvoiceByOCR
            const response = await DocumentService.CreateCustomsAgentInvoiceByOCR(formData, token);

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.parsed) {
                    // Use the existing document URL from documentUrls array
                    if (documentUrls.length > 0 && currentDocumentIndex < documentUrls.length) {
                        setUploadedDocumentUrl(documentUrls[currentDocumentIndex]);
                    }

                    // Update form with OCR results
                    setFormData(prev => {
                        const newData = {
                            ...prev,
                            invoiceNumber: result.parsed.invoice_number || prev.invoiceNumber,
                            invoiceDate: result.parsed.invoice_date ? new Date(result.parsed.invoice_date.split('/').reverse().join('-')) : prev.invoiceDate,
                            commission: result.parsed.commission?.toString() || prev.commission,
                            storageAtCustoms: result.parsed.storage_at_customs?.toString() || prev.storageAtCustoms,
                            seaAirFreight: result.parsed.sea_air_freight?.toString() || prev.seaAirFreight,
                            variousVatLiable: result.parsed.various_vat_liable?.toString() || prev.variousVatLiable,
                            variousVatExempt: result.parsed.various_vat_exempt?.toString() || prev.variousVatExempt,
                            roundingDifference: result.parsed.rounding_difference?.toString() || prev.roundingDifference
                        };

                        // Recalculate totals after OCR data update
                        // Calculate TotalVatLiable (variousVatLiable + storageAtCustoms + commission)
                        const variousVatLiable = parseFloat(newData.variousVatLiable) || 0;
                        const storageAtCustoms = parseFloat(newData.storageAtCustoms) || 0;
                        const commission = parseFloat(newData.commission) || 0;
                        newData.totalVatLiable = (variousVatLiable + storageAtCustoms + commission).toFixed(2);

                        // Calculate TotalIncludingVAT = SeaAirFreight + VariousVatExempt + TotalManifest + RoundingDifference
                        const seaAirFreight = parseFloat(newData.seaAirFreight) || 0;
                        const variousVatExempt = parseFloat(newData.variousVatExempt) || 0;
                        const totalManifest = parseFloat(newData.totalManifest) || 0;
                        const roundingDifference = parseFloat(newData.roundingDifference) || 0;
                        newData.totalIncludingVat = (seaAirFreight + variousVatExempt + totalManifest + roundingDifference).toFixed(2);

                        // Calculate TotalVAT using calculateVatAmount on TotalVatLiable
                        const totalVatLiable = parseFloat(newData.totalVatLiable) || 0;
                        const totalVat = calculateVatAmountV2(totalVatLiable, 100, newData.invoiceDate);
                        newData.totalVat = totalVat.toFixed(2);

                        // Calculate TotalToPay = TotalIncludingVAT + TotalVatLiable + TotalVAT
                        const totalIncludingVat = parseFloat(newData.totalIncludingVat) || 0;
                        newData.totalToPay = (totalIncludingVat + totalVatLiable + totalVat).toFixed(2);

                        return newData;
                    });

                    // Apply customs_agent_id / customs_agent from OCR if present
                    const parsedCustomsAgentId = (result.parsed.customs_agent_id || '').toString().trim();
                    if (parsedCustomsAgentId) {
                        const normalizeId = (s) => String(s || '').trim().replace(/\D/g, '');
                        const matchedOption = (accountCardOptions || []).find(
                            (opt) => opt.accountCaseId != null && normalizeId(opt.accountCaseId) === normalizeId(parsedCustomsAgentId)
                        );
                        if (matchedOption) {
                            handleInputChange('customsAgent', matchedOption.value);
                            handleInputChange('customsAgentId', matchedOption.accountCaseId || parsedCustomsAgentId);
                        } else {
                            toast.info(t('PleaseAddCustomsAgent'));
                            handleAddItemSelection('customsAgent', (result.parsed.customs_agent || '').toString().trim(), parsedCustomsAgentId);
                        }
                    }

                    // After successful OCR processing, switch back to manual mode to show the filled fields
                    setAutoFilling(false);
                    toast.success(t('CustomsAgentInvoiceProcessedSuccessfully'));
                }
            } else {
                const errorText = await response.text();
                console.error('Customs Agent Invoice OCR processing error:', errorText);
                toast.error(t('ErrorProcessingCustomsAgentInvoiceOCR'));
            }
        } catch (error) {
            console.error('Error processing Customs Agent Invoice OCR:', error);
            toast.error(t('ErrorProcessingCustomsAgentInvoiceOCR'));
        } finally {
            setIsProcessingCustomsAgentInvoiceOCR(false);
        }
    };

    const months = [
        { value: 1, label: t('January') },
        { value: 2, label: t('February') },
        { value: 3, label: t('March') },
        { value: 4, label: t('April') },
        { value: 5, label: t('May') },
        { value: 6, label: t('June') },
        { value: 7, label: t('July') },
        { value: 8, label: t('August') },
        { value: 9, label: t('September') },
        { value: 10, label: t('October') },
        { value: 11, label: t('November') },
        { value: 12, label: t('December') }
    ];

    const years = Array.from({ length: 15 }, (_, i) => new Date().getFullYear() - 5 + i);

    useEffect(() => {
        setShowPdfIframe(showPdfIframe)
    }, [showPdfIframe]);

    // Update document URL when document arrays or index changes
    useEffect(() => {
        const allDocuments = [...importManifestDocuments, ...customsAgentInvoiceDocuments];
        const allDocumentUrls = [...importManifestDocumentUrls, ...customsAgentInvoiceDocumentUrls];
        
        // Always update documentUrls to the combined array
        if (allDocumentUrls.length > 0) {
            // Add all URLs to active set
            allDocumentUrls.forEach(url => {
                if (url && typeof url === 'string') {
                    allActiveUrlsRef.current.add(url);
                }
            });
            
            setDocumentUrls(allDocumentUrls);
            
            // Update current document URL if index is valid
            if (currentDocumentIndex >= 0 && currentDocumentIndex < allDocumentUrls.length) {
                const targetUrl = allDocumentUrls[currentDocumentIndex];
                // Validate URL is a valid blob URL or string
                if (targetUrl && typeof targetUrl === 'string') {
                    activeUrlRef.current = targetUrl;
                    setUploadedDocumentUrl(targetUrl);
                }
            } else if (allDocumentUrls.length > 0 && currentDocumentIndex >= allDocumentUrls.length) {
                // Reset to first document if index is out of bounds
                const firstUrl = allDocumentUrls[0];
                activeUrlRef.current = firstUrl;
                setCurrentDocumentIndex(0);
                setUploadedDocumentUrl(firstUrl);
            }
        } else {
            // Clear URLs if no documents
            setDocumentUrls([]);
            setUploadedDocumentUrl(null);
            setCurrentDocumentIndex(0);
            activeUrlRef.current = null;
        }
    }, [currentDocumentIndex, importManifestDocumentUrls, customsAgentInvoiceDocumentUrls, importManifestDocuments, customsAgentInvoiceDocuments]);

    // Cleanup effect to prevent memory leaks
    useEffect(() => {
        return () => {
            // Clean up all document URLs when component unmounts
            // Revoke all URLs that are tracked in the ref
            allActiveUrlsRef.current.forEach(url => {
                if (url && typeof url === 'string' && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            allActiveUrlsRef.current.clear();
            activeUrlRef.current = null;
        };
    }, []);

    // Update month dates when year/month props change
    useEffect(() => {
        if (year && month) {
            const newReportingMonthDate = new Date(Date.UTC(year, month - 1, 10));
            const newRegistrationMonthDate = new Date(Date.UTC(year, month - 1, 10));
            setFormData(prev => ({
                ...prev,
                reportingMonthDate: newReportingMonthDate,
                registrationMonthDate: newRegistrationMonthDate
            }));
        }
    }, [year, month]);

    return (
        <StyledModal
            open={open}
            onClose={handleAttemptClose}
            aria-labelledby="import-manifest-modal-title"
        >
            <ModalContent direction={direction} hasIframe={showPdfIframe && uploadedDocumentUrl}>
                {/* PDF Iframe - Only show when showPdfIframe is true and there's an uploaded document */}
                <PDFViewer
                    showPdfIframe={showPdfIframe}
                    uploadedDocumentUrl={uploadedDocumentUrl}
                    documents={[...importManifestDocuments, ...customsAgentInvoiceDocuments]}
                    currentDocumentIndex={currentDocumentIndex}
                    handlePreviousDocument={handlePreviousDocument}
                    handleNextDocument={handleNextDocument}
                    t={t}
                />

                {/* Form Content */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box dir={direction} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h5" component="h2" sx={{ color: '#304FFF', fontWeight: 'bold', flex: 1, textAlign: 'center' }}>
                            {t('ImportManifestEntry')}
                        </Typography>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Iframe visibility toggle button */}
                            {uploadedDocumentUrl && (
                                <IconButton onClick={handleToggleIframe}>
                                    {showPdfIframe ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            )}
                            <IconButton
                                onClick={handleAttemptClose}
                                sx={{
                                    fontWeight: 'bold',
                                    color: '#304FFF',
                                    '&:hover': {
                                        backgroundColor: 'rgba(48, 79, 255, 0.1)'
                                    }
                                }}
                            >
                                <CloseIcon sx={{ fontWeight: 'bold' }} />
                            </IconButton>
                        </div>
                    </Box>

                    {/* Automatic/Manual Filling Toggle Buttons */}
                    <ToggleButtons
                        autoFilling={autoFilling}
                        setAutoFilling={setAutoFilling}
                        t={t}
                        direction={direction}
                        formData={formData}
                        reportingMonthDatePickerKey={reportingMonthDatePickerKey}
                        handleReportingMonthDateChange={handleReportingMonthDateChange}
                        handleRegistrationMonthDateChange={handleRegistrationMonthDateChange}
                    />

                    {/* OCR Section - Only show when autoFilling is true */}
                    <OCRSection
                        autoFilling={autoFilling}
                        direction={direction}
                        t={t}
                        importManifestDocuments={importManifestDocuments}
                        setImportManifestDocuments={setImportManifestDocuments}
                        handleImportManifestDocumentUpload={handleImportManifestDocumentUpload}
                        customsAgentInvoiceDocuments={customsAgentInvoiceDocuments}
                        setCustomsAgentInvoiceDocuments={setCustomsAgentInvoiceDocuments}
                        handleCustomsAgentInvoiceDocumentUpload={handleCustomsAgentInvoiceDocumentUpload}
                        isProcessingImportManifestOCR={isProcessingImportManifestOCR}
                        isProcessingCustomsAgentInvoiceOCR={isProcessingCustomsAgentInvoiceOCR}
                        handleProcessOCR={handleProcessOCR}
                        handleProcessCustomsAgentInvoiceOCR={handleProcessCustomsAgentInvoiceOCR}
                    />

                    {/* Manual Form Fields - Only show when autoFilling is false */}
                    {!autoFilling && (
                        <Grid container spacing={2}>
                            {/* Left Column */}
                            <Grid item xs={12} md={6}>
                                <AccountsSection
                                    direction={direction}
                                    t={t}
                                    formData={formData}
                                    handleInputChange={handleInputChange}
                                    validationErrors={validationErrors}
                                    accountCardOptions={accountCardOptions}
                                    showPdfIframe={showPdfIframe}
                                    uploadedDocumentUrl={uploadedDocumentUrl}
                                    onAddItemSelection={handleAddItemSelection}
                                />

                                <CustomsAgentInvoiceSection
                                    direction={direction}
                                    t={t}
                                    formData={formData}
                                    handleInputChange={handleInputChange}
                                    validationErrors={validationErrors}
                                />
                            </Grid>

                            {/* Right Column */}
                            <Grid item xs={12} md={6}>
                                <SortingCodesSection
                                    direction={direction}
                                    t={t}
                                    formData={formData}
                                    handleInputChange={handleInputChange}
                                    validationErrors={validationErrors}
                                    sortCodeIdOptions={sortCodeIdOptions}
                                    showPdfIframe={showPdfIframe}
                                    uploadedDocumentUrl={uploadedDocumentUrl}
                                />

                                <ImportManifestDetailsSection
                                    direction={direction}
                                    t={t}
                                    formData={formData}
                                    handleInputChange={handleInputChange}
                                    validationErrors={validationErrors}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* Totals and Actions Section */}
                    <TotalsAndActionsSection
                        t={t}
                        formData={formData}
                        handleUpdate={handleUpdate}
                        onClose={handleAttemptClose}
                        onClear={clearAllFields}
                    />
                </div>
                {client && client.caseId && token && (
                    <AddAccountCardModal
                        accountCardOptions={accountCardOptions}
                        open={editedAddAccountCard}
                        onClose={() => setEditedAddAccountCard(false)}
                        editedItem={newAccountCard || {}}
                        isEdit={isEditAccountCard}
                        isRange={false}
                        isClosedReportingMonth={false}
                        palCodes={palCodes || []}
                        propPalCode=""
                        direction={direction}
                        isAdd={true}
                        client={client}
                        token={token}
                        setLoading={setLoading || (() => { })}
                        handleChangeAfterAdd={handleChangeAfterAdd}
                        fetchEnums={fetchEnums || (async () => { })}
                        type={addAccountCardType}
                    />
                )}
            </ModalContent>
        </StyledModal>
    );
};

export default ImportManifestEntryModal;